import { Injectable } from '@angular/core';
import { OccEndpointsService, TranslationService } from '@spartacus/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import {
  CartOrder,
  OrderRequestConfig,
  OrdersRequestConfig,
  OrdersStatusRequest,
  OrderStatusRequest,
} from '../../../../core/models/order.model';
import { failStatus, successStatus } from '../../../../core/models/request-status.model';
import { Order } from '@spartacus/order/root';
import { OrderEntry } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root',
})
export class CustomOrderAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
  ) {}

  getOrders(ordersRequestConfig: OrdersRequestConfig): Observable<OrdersStatusRequest> {
    const url = this.occEndPointService.buildUrl('getOrders', {
      queryParams: {
        fromDate: ordersRequestConfig.fromDate,
        toDate: ordersRequestConfig.toDate,
        openOnly: ordersRequestConfig.openOnly,
        poNumber: ordersRequestConfig.poNumber,
        orderNumber: ordersRequestConfig.orderNumber,
      },
    });

    return this.http.get(url).pipe(
      map((response: any) => ({
        // orders: ordersMock,
        orders: this.normalizeOrders(response),
        ...successStatus,
      })),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  getOrdersExport(ordersRequestConfig: OrdersRequestConfig): Observable<any> {

    const url = this.occEndPointService.buildUrl('getOrdersExport', {
      urlParams: {
        dateFrom: ordersRequestConfig.fromDate,
        dateTo: ordersRequestConfig.toDate,
      },
    });

    return this.http.get(url, { responseType: 'json' });
  }

  getOrder(ordersRequestConfig: OrderRequestConfig): Observable<OrderStatusRequest> {
    const url = this.occEndPointService.buildUrl('getOrder', {
      urlParams: {
        uid: ordersRequestConfig.uid,
      },
    });

    return this.http.get(url).pipe(
      map((response: any) => ({
        order: this.normalizeOrder(response),
        ...successStatus,
      })),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  normalizeOrders(_orders: any): Order[] {
    const orders: Order[] = [];

    (_orders || []).forEach((order: any) => {
      orders.push({
        uid: order?.uid || '',
        code: order?.orderNumber || '',
        created: order?.orderDate ? new Date(order?.orderDate) : undefined,
        statusDisplay: order?.orderStatus || '',
        purchaseOrderNumber: order?.poNumber || '',
        deliveryItemsQuantity: order?.quantity || 0,
        totalPrice: {
          value: order?.total?.value || 0,
          formattedValue: order?.total?.formattedValue || '',
        },
      });
    });

    return orders;
  }

  normalizeOrder(_order: any): CartOrder {
    const entries: OrderEntry[] = [];

    (_order?.lines || []).forEach((entry: any) => {
      entries.push({
        product: entry?.product,
        notes: entry?.notes || '',
        status: entry?.lineStatus || '',
        quantity: entry?.quantityOrdered || 0,
        invoice: entry?.invoiceNumber || '',
        requestedShipDate: entry.requestDate ? new Date(entry.requestDate) : undefined,
        estimatedShipDate: entry.estimatedShipDate ? new Date(entry.estimatedShipDate).toString() : undefined,
        actualShipDate: entry.actualShipDate ? new Date(entry.actualShipDate) : undefined,
        trackingInfo: entry?.trackingInfo || '',
        poLine: entry?.lineNumber || '',
        warehouse: entry?.warehouse || '',

        basePrice: {
          value: entry?.basePrice?.value || 0,
          formattedValue: entry?.basePrice?.formattedValue || '',
        },

        totalPrice: {
          value: entry?.totalPrice?.value || 0,
          formattedValue: entry?.totalPrice?.formattedValue || '',
        },
      });
    });

    return {
      code: _order?.orderNumber || '',
      created: _order?.orderDate ? new Date(_order?.orderDate) : undefined,
      statusDisplay: _order?.orderStatus || '',

      accountId: _order?.accountId || '',
      accountName: _order?.accountName || '',

      purchaseOrderNumber: _order?.poNumber || '',
      customerPurchaseOrderNumber: _order?.customerPONumber || '',

      shippingPreference: _order?.shippingPreference,
      deliveryAddress: _order?.shippingAddress,
      billingAddress: _order?.billingAddress,
      shippingPriority: _order.shippingPriority,

      entries: entries,
      subTotal: _order?.totalLines,
      deliveryCost: _order?.totalShipping,
      totalPriceWithTax: _order?.total,
    };
  }
}
